import { mutationOptions } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { authAPIClient } from '../authAPIClient';
import { AUTH_QUERY_KEYS } from './keys';
import { USER_QUERY_KEYS } from '../../user/utils/keys';
import type {
  GenerateOtpRequest,
  GenerateOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  AssociateOtpRequest,
  AssociateOtpResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  RegisterRequest,
  RegisterResponse,
  RegisterWithPasswordRequest,
  RegisterWithPasswordResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  CheckEmailRequest,
  CheckEmailResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from './types';

export const AUTH_QUERIES = {
  generateOtp: () =>
    mutationOptions<GenerateOtpResponse, unknown, GenerateOtpRequest>({
      mutationKey: AUTH_QUERY_KEYS.generateOtp(),
      mutationFn: (payload) => authAPIClient.generateOtp(payload),
    }),
  verifyOtp: (queryClient: QueryClient) =>
    mutationOptions<VerifyOtpResponse, unknown, VerifyOtpRequest>({
      mutationKey: AUTH_QUERY_KEYS.verifyOtp(),
      mutationFn: (payload) => authAPIClient.verifyOtp(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.default() });
      },
    }),
  associateOtp: (queryClient: QueryClient) =>
    mutationOptions<AssociateOtpResponse, unknown, AssociateOtpRequest>({
      mutationKey: AUTH_QUERY_KEYS.associateOtp(),
      mutationFn: (payload) => authAPIClient.associateOtp(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.default() });
      },
    }),
  verifyEmail: (queryClient: QueryClient) =>
    mutationOptions<VerifyEmailResponse, unknown, VerifyEmailRequest>({
      mutationKey: AUTH_QUERY_KEYS.verifyEmail(),
      mutationFn: (payload) => authAPIClient.verifyEmail(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.default() });
      },
    }),
  register: (queryClient: QueryClient) =>
    mutationOptions<RegisterResponse, unknown, RegisterRequest>({
      mutationKey: AUTH_QUERY_KEYS.register(),
      mutationFn: (payload) => authAPIClient.register(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.default() });
      },
    }),
  registerWithPassword: (queryClient: QueryClient) =>
    mutationOptions<RegisterWithPasswordResponse, unknown, RegisterWithPasswordRequest>({
      mutationKey: AUTH_QUERY_KEYS.registerWithPassword(),
      mutationFn: (payload) => authAPIClient.registerWithPassword(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.default() });
      },
    }),
  login: (queryClient: QueryClient) =>
    mutationOptions<LoginResponse, unknown, LoginRequest>({
      mutationKey: AUTH_QUERY_KEYS.login(),
      mutationFn: (payload) => authAPIClient.login(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.default() });
      },
    }),
  logout: () =>
    mutationOptions<LogoutResponse, unknown, void>({
      mutationKey: AUTH_QUERY_KEYS.logout(),
      mutationFn: () => authAPIClient.logout(),
    }),
  checkEmail: () =>
    mutationOptions<CheckEmailResponse, unknown, CheckEmailRequest>({
      mutationKey: AUTH_QUERY_KEYS.checkEmail(),
      mutationFn: (payload) => authAPIClient.checkEmail(payload),
    }),
  // No invalidation: WordPress rotates the session on a password change, so the client logs out next.
  changePassword: () =>
    mutationOptions<ChangePasswordResponse, unknown, ChangePasswordRequest>({
      mutationKey: AUTH_QUERY_KEYS.changePassword(),
      mutationFn: (payload) => authAPIClient.changePassword(payload),
    }),
};
